import { Injectable } from '@angular/core';
import { AuthService } from '@app/core/auth/auth.service';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { cloneDeep } from 'lodash-es';
import { FuseNavigationItem } from '@src/@fuse/components/navigation';
import { Navigation } from '@app/core/navigation/navigation.types';
import { mergeMap } from 'rxjs/operators';
import { compactNavigation, defaultNavigation, futuristicNavigation, horizontalNavigation } from './nav-data/data';
import { GwcApiInstitutionsService } from '@src/app/services/gwc-api/institutions.service';

@Injectable({
    providedIn: 'root'
})
export class NavigationService
{
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private auth: AuthService,
        private _gwcApiInstitutionsService: GwcApiInstitutionsService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        if (this.auth.isOperator) {
            const items: FuseNavigationItem[] = [{id: 'operator', title: 'Student orders', type: 'basic', link: '/operator'}];
            const navigation = {compact: items, default: items, futuristic: items, horizontal: items};
            this._navigation.next(navigation);
            return of(navigation);
        }

        return this._gwcApiInstitutionsService.getInstitutions( { institution_type: 'UNIVERSITY,POLYTECH,SECONDARY_SCHOOL,OTHER_TERTIARY' } ).pipe(
            mergeMap(institutions => {

                // Fill compact navigation children using the default navigation
                this._compactNavigation.forEach((compactNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if(defaultNavItem.id == 'institution') {
                            compactNavItem.children = this._generateInstitutionLinks(institutions)
                        } else if ( defaultNavItem.id === compactNavItem.id ) {
                            compactNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill futuristic navigation children using the default navigation
                this._futuristicNavigation.forEach((futuristicNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if(defaultNavItem.id == 'institution') {
                            futuristicNavItem.children = this._generateInstitutionLinks(institutions)
                        } else if ( defaultNavItem.id === futuristicNavItem.id )
                        {
                            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill horizontal navigation children using the default navigation
                this._horizontalNavigation.forEach((horizontalNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if(defaultNavItem.id == 'institution') {
                            horizontalNavItem.children = this._generateInstitutionLinks(institutions)
                        } else if ( defaultNavItem.id === horizontalNavItem.id )
                        {
                            horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                this._defaultNavigation.forEach((defaultNavItem) => {
                    if(defaultNavItem.id == 'institution') {
                        defaultNavItem.children = this._generateInstitutionLinks(institutions)
                    }
                })

                // Return the response
                return of(
                    {
                        compact   : cloneDeep(this._compactNavigation),
                        default   : cloneDeep(this._defaultNavigation),
                        futuristic: cloneDeep(this._futuristicNavigation),
                        horizontal: cloneDeep(this._horizontalNavigation)
                    }
                ).pipe(
                    tap((navigation) => {
                        if (this.auth.user?.role === 'super_administrator') {
                            for (const items of Object.values(navigation)) {
                                items.push({id: 'api-access', title: 'API access', type: 'basic', icon: 'heroicons_outline:key', link: '/system/api-keys'});
                            }
                        }
                        this._navigation.next(navigation)
                    })
                ) as Observable<Navigation>


            })
        )
    }


    private _generateInstitutionLinks(institutions): FuseNavigationItem[] {
        // Sort institutions: ARA Institute, Lincoln University, University of Canterbury first, then alphabetically
        const sortedInstitutions = institutions.sort((a, b) => {
            const nameA = a.institution.toLowerCase();
            const nameB = b.institution.toLowerCase();

            // Define priority order
            const getPriorityIndex = (name: string) => {
                if (name.includes('ara institute of canterbury') || name.includes('ara institute')) {
                    return 0;
                }
                if (name.includes('lincoln university')) {
                    return 1;
                }
                if (name.includes('university of canterbury') && !name.includes('ara institute')) {
                    return 2;
                }
                return -1; // Not a priority institution
            };

            const indexA = getPriorityIndex(nameA);
            const indexB = getPriorityIndex(nameB);

            // If both are priority institutions, sort by their priority index
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }

            // If only A is priority, A comes first
            if (indexA !== -1 && indexB === -1) {
                return -1;
            }

            // If only B is priority, B comes first
            if (indexA === -1 && indexB !== -1) {
                return 1;
            }

            // For all other institutions, sort alphabetically
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
        });

        // Separate priority institutions from others
        const priorityInstitutions = [];
        const otherInstitutions = [];

        sortedInstitutions.forEach(inst => {
            const name = inst.institution.toLowerCase();
            if (name.includes('ara institute') || 
                name.includes('lincoln university') || 
                (name.includes('university of canterbury') && !name.includes('ara institute'))) {
                priorityInstitutions.push(inst);
            } else {
                otherInstitutions.push(inst);
            }
        });

        // Create navigation items for priority institutions
        const priorityNavItems: FuseNavigationItem[] = priorityInstitutions.map(inst => ({
            id: `institution.${inst.institution_id}`,
            title: inst.institution,
            type: 'basic' as const,
            icon: 'heroicons_outline:library',
            link: `/institution/${inst.institution_id}`
        }));

        // Create collapsible group for other institutions
        const otherInstitutionsGroup: FuseNavigationItem = {
            id: 'institutions.others',
            title: 'Other Institutions',
            type: 'collapsable' as const,
            icon: 'heroicons_outline:folder',
            children: otherInstitutions.map(inst => ({
                id: `institution.${inst.institution_id}`,
                title: inst.institution,
                type: 'basic' as const,
                icon: 'heroicons_outline:library',
                link: `/institution/${inst.institution_id}`
            }))
        };

        // Combine priority institutions with the collapsible group
        return [...priorityNavItems, otherInstitutionsGroup];
    }


}
